// Consent-gated analytics. Loads Google Analytics (gtag) ONLY after the user
// accepts cookies AND a measurement id is configured (REACT_APP_GA_ID).
// Without a GA id it stays completely dormant (no network calls, no cookies).

const GA_ID = process.env.REACT_APP_GA_ID;
const CONSENT_KEY = "wa.cookie-consent";
let initialized = false;

export function hasAnalyticsConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "all";
  } catch {
    return false;
  }
}

export function initAnalytics() {
  if (initialized || !GA_ID || !hasAnalyticsConsent()) return;
  initialized = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { anonymize_ip: true });
}

export function trackPage(path) {
  if (!initialized || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", { page_path: path });
}

// First-party, cookieless pageview logging to our own backend. Runs regardless
// of Google Analytics / cookie consent: no cookies or persistent identifiers are
// stored client-side. Unique visitors are derived server-side from a pseudonymized
// /24 IP hash. The session id below lives only in memory for this tab session.
const SESSION_ID =
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;

// Automated browsers (Playwright, Puppeteer, Selenium, Lighthouse) must never
// inflate human analytics. The backend has its own bot filter; this prevents
// the request from being made at all so we save bandwidth too.
function _isAutomated() {
  try {
    if (navigator && navigator.webdriver) return true;
    const ua = (navigator && navigator.userAgent) || "";
    if (/headless|phantomjs|puppeteer|playwright|lighthouse/i.test(ua)) return true;
    return false;
  } catch {
    return false;
  }
}

// In-memory last-pageview dedup. Rejects the exact same path within 1500ms —
// catches React StrictMode double-fire and quick re-renders without blocking
// legitimate repeat navigations (e.g. /woning/a → /woningen → /woning/a).
let _lastPvPath = null;
let _lastPvTs = 0;
const _PV_DEDUP_MS = 1500;

export function trackPageviewServer(path) {
  try {
    if (!path) return;
    // Admin paths are not public analytics.
    if (path.startsWith("/admin")) return;
    if (_isAutomated()) return;
    const now = Date.now();
    if (path === _lastPvPath && now - _lastPvTs < _PV_DEDUP_MS) return;
    _lastPvPath = path;
    _lastPvTs = now;
    let ref = "";
    try {
      ref = document.referrer ? new URL(document.referrer).host : "";
    } catch {
      ref = "";
    }
    // Pull UTM parameters from the URL so lifecycle / paid / social clicks can
    // be attributed to their campaign server-side (no cookies, no fingerprint).
    const utm = {};
    try {
      const qs = new URLSearchParams(window.location.search);
      for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
        const v = qs.get(k);
        if (v) utm[k] = v.slice(0, 64);
      }
    } catch {
      /* ignore */
    }
    _send({
      event_type: "screen_view",
      event_name: "page_view",
      screen: path,
      props: { platform: "web", ref, ...utm },
    });
  } catch {
    /* never break navigation on analytics failure */
  }
}

// Fire a lightweight custom event (marketing nudges, CTA clicks). Reuses the
// existing session id and same backend endpoint. Never blocks the UI on failure.
export function trackEvent(name, props = {}) {
  _send({
    event_type: "action",
    event_name: name,
    screen: typeof window !== "undefined" ? window.location.pathname : null,
    props: { platform: "web", ...props },
  });
}

// -----------------------------------------------------------------------------
// Deep behavior tracking: clicks + page exits (dwell time).
// -----------------------------------------------------------------------------
const _BASE = () => process.env.REACT_APP_BACKEND_URL || "";

// Include Bearer token when the user is logged in so backend can attribute the
// event to a user_id. `sendBeacon` cannot set headers, so for authenticated
// events we prefer keepalive-fetch and fall back to sendBeacon only for
// anonymous tracking (page_exit on unload).
function _authToken() {
  try { return localStorage.getItem("wa_token"); } catch { return null; }
}

function _send(event) {
  try {
    const body = JSON.stringify({ events: [{ session_id: SESSION_ID, ...event }] });
    const url = `${_BASE()}/api/analytics/track`;
    const token = _authToken();
    if (token) {
      // Authenticated: use fetch with Authorization header so backend attributes user_id.
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Platform": "web",
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: true,
      }).catch(() => {});
      return;
    }
    // Anonymous: sendBeacon is more reliable during unload.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Client-Platform": "web" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* silent */
  }
}

// Track a click on a known element (label, testid, href, tag).
export function trackClick(payload) {
  _send({
    event_type: "click",
    event_name: "click",
    screen: typeof window !== "undefined" ? window.location.pathname : null,
    props: { platform: "web", ...payload },
  });
}

// Track a page exit with dwell time (ms). Called on route change or pagehide.
export function trackPageExit(path, dwellMs) {
  _send({
    event_type: "page_exit",
    event_name: "page_exit",
    screen: path,
    props: { platform: "web", dwell_ms: Math.max(0, Math.round(dwellMs || 0)) },
  });
}

// Global capture-phase click listener. Idempotent (installs once). Skips
// clicks on the admin area to avoid noise from internal usage.
let _clickInstalled = false;
export function installGlobalClickTracker() {
  if (_clickInstalled || typeof document === "undefined") return;
  _clickInstalled = true;
  document.addEventListener(
    "click",
    (ev) => {
      try {
        // Ignore admin sessions — we don't want to bloat data with internal clicks.
        if (window.location.pathname.startsWith("/admin")) return;
        const target = ev.target && ev.target.closest
          ? ev.target.closest("a, button, [role='button'], [data-track]")
          : null;
        if (!target) return;
        const testid = target.getAttribute("data-testid") || "";
        const track = target.getAttribute("data-track") || "";
        const href = target.getAttribute("href") || "";
        const tag = target.tagName ? target.tagName.toLowerCase() : "";
        // Label: use aria-label > inner text (trimmed) > title
        const rawLabel =
          target.getAttribute("aria-label") ||
          (target.innerText || "").trim() ||
          target.getAttribute("title") ||
          "";
        const label = rawLabel.replace(/\s+/g, " ").slice(0, 120);
        trackClick({ testid, track, href, tag, label });
      } catch {
        /* silent */
      }
    },
    true // capture phase so we catch clicks even if stopPropagation is called
  );
}
